// frontend/src/components/layout/Sidebar.jsx

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useInAppSyncStatus } from '../../hooks/useInAppSyncStatus';
import { forecastsMissing } from '../../utils/syncGate';
import { ICON } from '../../icons';
import DisclosureCaret from '../common/DisclosureCaret';
import { PageIcon } from '../../config/appPages';
import TabLink from '../../tabs/TabLink';
import { isNavPathActive, isNavGroupActive } from './navActive';
import { sectionPaths, sidebarMenu } from './navMenus';
import SyncProgressPill from './SyncProgressPill';
import './Sidebar.css';

const NavLink = ({ to, sub, children }) => {
  const { pathname } = useLocation();
  return (
    <TabLink
      to={to}
      className={`sidebar-link ${sub ? 'sidebar-sub-link' : ''} ${isNavPathActive(to, pathname) ? 'active' : ''}`}
    >
      {children}
    </TabLink>
  );
};

// A top-level destination. Its glyph is the page's own (config/appPages.js),
// so the sidebar and the command palette draw the same one.
const PageLink = ({ to, label }) => (
  <NavLink to={to}>
    <PageIcon path={to} size={ICON.lg} className="sidebar-icon" />
    {label}
  </NavLink>
);

const SubLink = ({ to, label }) => <NavLink to={to} sub>{label}</NavLink>;

// A collapsible nav section: open by the user's saved choice, or — with none
// saved — while the current route is inside it. Both lookups go through
// navActive, so a link stays lit on the segment routes that are the same
// screen filtered or scoped to a record (/item-setup/item/:itemCode,
// /purchase-orders/:id), and never on a sibling that merely shares a prefix
// ('/forecast' and the staff '/forecast-generator'). The links stay mounted so
// the group can slide; `inert` takes a closed group out of focus and the
// accessibility tree, and the CSS collapses it off the same attribute.
const SidebarSection = ({ section, label, children }) => {
  const { id, icon: Icon } = section;
  const { pathname } = useLocation();
  const { userContext, updatePreference } = useAuth();
  const routeInside = isNavGroupActive(sectionPaths(section), pathname);
  const open = userContext?.user?.ui_preferences?.sidebar_sections?.[id] ?? routeInside;

  // Functional updater — flipping the stored value via `prev` avoids races
  // when several sections toggle quickly.
  const toggle = () => {
    updatePreference('sidebar_sections', (prev) => ({
      ...(prev || {}),
      [id]: !(prev?.[id] ?? routeInside),
    }));
  };

  return (
    <div>
      <button
        className={`sidebar-section-label ${routeInside ? 'active' : ''}`}
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon size={ICON.lg} className="sidebar-icon" />
        <span className="sidebar-section-text">{label}</span>
        <DisclosureCaret open={open} size={ICON.sm} className="sidebar-caret" />
      </button>
      <div className="sidebar-sub-group" inert={!open}>
        <div className="sidebar-sub-group-clip">
          <div className="sidebar-sub-group-links">{children}</div>
        </div>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const { t } = useTranslation();
  const forecastOffered = !forecastsMissing(useInAppSyncStatus());

  return (
    <nav className="sidebar">
      <div className="sidebar-nav">
        {sidebarMenu({ forecastOffered }).map((entry) => (entry.links ? (
          <SidebarSection key={entry.id} section={entry} label={t(entry.labelKey)}>
            {entry.links.map((link) => <SubLink key={link.path} to={link.path} label={t(link.labelKey)} />)}
          </SidebarSection>
        ) : (
          <PageLink key={entry.path} to={entry.path} label={t(entry.labelKey)} />
        )))}
      </div>
      {/* Bottom of the column (`margin-top: auto`); renders nothing unless
          an import is on its way, failed, or finished in this session. */}
      <SyncProgressPill />
    </nav>
  );
};

export default Sidebar;
